import { dotvotingApi } from '../../services/dotvotingApi';
import useUserStore from '../userStore';
import {
  hasBoardAccess,
  hasBoardUser,
  normalizeError,
  publishBoardChange,
  syncViewCard,
} from './helpers';
import type { BoardState, BoardStoreGet, BoardStoreSet } from './types';
import { trackAnalyticsOnce } from '@dotstorming/analytics';

const trackCardCreated = (
  boardId: string,
  boardDetails: BoardState['boardDetails'],
  cardType: string
) => {
  if (boardDetails?.isOwner) {
    trackAnalyticsOnce('first_card_added', 'first_card_added', {
      card_type: cardType,
      space_type: 'board',
      creation_source: 'board',
    });
    return;
  }

  trackParticipantContribution(boardId, boardDetails, 'card');
};

const trackParticipantContribution = (
  boardId: string,
  boardDetails: BoardState['boardDetails'],
  contributionType: 'card' | 'comment' | 'vote' | 'chat'
) => {
  if (boardDetails?.isOwner) return;

  const participantType = useUserStore.getState().boardSession.isGuest ? 'guest' : 'member';
  trackAnalyticsOnce(`participant_contributed_${boardId}`, 'participant_contributed', {
    contribution_type: contributionType,
    participant_type: participantType,
  });
};

const applyCardOrderToIdeas = (ideas: BoardState['ideas'], cardOrder: string[]) =>
  ideas.map((idea) => {
    const nextSortNo = cardOrder.indexOf(idea.id) + 1;

    return {
      ...idea,
      sortNo: nextSortNo === 0 ? cardOrder.length + 1 : nextSortNo,
    };
  });

const canCurrentUserAddIdeas = (boardDetails: BoardState['boardDetails']) =>
  boardDetails?.participantUploadsEnabled !== false;

export const createIdeaActions = (
  set: BoardStoreSet,
  get: BoardStoreGet
): Pick<
  BoardState,
  | 'addIdea'
  | 'addIdeaImage'
  | 'addIdeaVideo'
  | 'addIdeaFile'
  | 'approveIdea'
  | 'updateIdea'
  | 'updateImageIdea'
  | 'togglePinned'
  | 'moveIdeaToColumn'
  | 'removeIdea'
  | 'addVote'
  | 'removeVote'
  | 'addComment'
  | 'removeComment'
  | 'reorderIdeaSortOrder'
  | 'addChatMessage'
  | 'setViewCard'
> => ({
  addIdea: async (content, options) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      boardDetails?.locked ||
      !hasBoardUser() ||
      !canCurrentUserAddIdeas(boardDetails)
    ) {
      return;
    }

    try {
      const idea = await dotvotingApi.createIdea(boardId, content, options);
      set((state) => ({
        ideas: [idea, ...state.ideas],
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaAdded', {
        idea: {
          id: idea.id,
          type: idea.type,
          ownerId: idea.ownerId,
          title: idea.title,
          content: idea.content,
          processingStatus: idea.processingStatus,
          approved: idea.approved,
          suggested: idea.suggested,
          sortNo: idea.sortNo,
          columnId: idea.columnId,
        },
      });
      trackCardCreated(boardId, boardDetails, idea.type);
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  addIdeaImage: async (file, title, options) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      boardDetails?.locked ||
      !hasBoardUser() ||
      !canCurrentUserAddIdeas(boardDetails)
    ) {
      return;
    }

    try {
      const files = Array.isArray(file) ? file : [file];
      const uploadedIdeas = await Promise.all(
        files.map((currentFile) =>
          dotvotingApi.uploadImageIdea(boardId, currentFile, title, options)
        )
      );
      const ideas = uploadedIdeas.flat();
      set((state) => ({
        ideas: [...ideas, ...state.ideas],
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaAttachmentInserted', {
        ideaIds: ideas.map((idea) => idea.id),
      });
      if (ideas.length > 0) trackCardCreated(boardId, boardDetails, ideas[0].type);
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  addIdeaVideo: async (file, title, options) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      boardDetails?.locked ||
      !hasBoardUser() ||
      !canCurrentUserAddIdeas(boardDetails)
    ) {
      return;
    }

    try {
      const ideas = await dotvotingApi.uploadVideoIdea(boardId, file, title, options);
      set((state) => ({
        ideas: [...ideas, ...state.ideas],
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaAttachmentInserted', {
        ideaIds: ideas.map((idea) => idea.id),
      });
      if (ideas.length > 0) trackCardCreated(boardId, boardDetails, ideas[0].type);
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  addIdeaFile: async (file, title, options) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      boardDetails?.locked ||
      !hasBoardUser() ||
      !canCurrentUserAddIdeas(boardDetails)
    ) {
      return;
    }

    try {
      const ideas = await dotvotingApi.uploadFileIdea(boardId, file, title, options);
      set((state) => ({
        ideas: [...ideas, ...state.ideas],
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaAttachmentInserted', {
        ideaIds: ideas.map((idea) => idea.id),
      });
      if (ideas.length > 0) trackCardCreated(boardId, boardDetails, ideas[0].type);
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  approveIdea: async (ideaId) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.isOwner || !hasBoardAccess()) {
      return;
    }

    try {
      await dotvotingApi.approveIdea(boardId, ideaId);
      set((state) => ({
        ideas: state.ideas.map((idea) => (idea.id === ideaId ? { ...idea, approved: true } : idea)),
        viewCard:
          state.viewCard?.id === ideaId ? { ...state.viewCard, approved: true } : state.viewCard,
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaApproved', { ideaId });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  updateIdea: async (ideaId, updates) => {
    const { boardId } = get();
    if (!boardId || !hasBoardAccess()) {
      return;
    }

    const hasContentUpdate = typeof updates.content === 'string';
    const hasTitleUpdate = typeof updates.title === 'string';

    if (!hasContentUpdate && !hasTitleUpdate) {
      return;
    }

    const updatePayload: { content?: string; title?: string } = {};
    if (hasContentUpdate) {
      updatePayload.content = updates.content;
    }
    if (hasTitleUpdate) {
      updatePayload.title = updates.title;
    }

    try {
      await dotvotingApi.updateIdea(boardId, ideaId, updatePayload);
      set((state) => {
        const updatedIdeas = state.ideas.map((idea) => {
          if (idea.id !== ideaId) {
            return idea;
          }

          return {
            ...idea,
            ...(hasContentUpdate ? { content: updates.content } : {}),
            ...(hasTitleUpdate ? { title: updates.title } : {}),
          };
        });

        return {
          ideas: updatedIdeas,
          viewCard: syncViewCard(updatedIdeas, state.viewCard),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicIdeaUpdated', {
        idea: {
          id: ideaId,
          topicId: boardId,
          ...updatePayload,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  updateImageIdea: async (ideaId, file, title, mimeType) => {
    const { boardId } = get();
    if (!boardId || !hasBoardAccess()) {
      return;
    }

    try {
      const updatedIdea = await dotvotingApi.updateImageIdea(
        boardId,
        ideaId,
        file,
        title,
        mimeType
      );
      set((state) => {
        const updatedIdeas = state.ideas.map((idea) => {
          if (idea.id !== ideaId) {
            return idea;
          }

          return {
            ...idea,
            content: updatedIdea.content,
            title: updatedIdea.title,
            mimeType: updatedIdea.mimeType,
          };
        });

        return {
          ideas: updatedIdeas,
          viewCard: syncViewCard(updatedIdeas, state.viewCard),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicIdeaUpdated', {
        idea: {
          id: ideaId,
          topicId: boardId,
          content: updatedIdea.content,
          title: updatedIdea.title,
          mimeType: updatedIdea.mimeType,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
      throw error;
    }
  },
  togglePinned: async (ideaId) => {
    const { boardId, ideas, boardDetails } = get();
    const currentUser = useUserStore.getState().getCurrentUser();
    if (!boardId || !currentUser) {
      return;
    }
    if (boardDetails?.cardSorting === 'custom') {
      return;
    }

    const idea = ideas.find((candidate) => candidate.id === ideaId);
    if (!idea) {
      return;
    }

    const nextPinned = !idea.pinned;

    try {
      await dotvotingApi.setPinned(boardId, ideaId, nextPinned);
      set((state) => ({
        ideas: state.ideas.map((candidate) =>
          candidate.id === ideaId ? { ...candidate, pinned: nextPinned } : candidate
        ),
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicIdeaPinned', { ideaId, pinned: nextPinned });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  moveIdeaToColumn: async (ideaId, columnId) => {
    const { boardId, boardDetails, ideas } = get();
    if (
      !boardId ||
      !boardDetails?.isOwner ||
      boardDetails.locked ||
      boardDetails.layout !== 'columns' ||
      !hasBoardAccess()
    ) {
      return;
    }

    const validColumnIds = new Set((boardDetails.columns ?? []).map((column) => column.id));
    if (!validColumnIds.has(columnId)) {
      return;
    }

    const idea = ideas.find((candidate) => candidate.id === ideaId);
    if (!idea || idea.columnId === columnId) {
      return;
    }

    const previousIdeas = ideas;
    const updatedIdeas = ideas.map((candidate) =>
      candidate.id === ideaId ? { ...candidate, columnId } : candidate
    );

    set({
      ideas: updatedIdeas,
      viewCard: syncViewCard(updatedIdeas, get().viewCard),
      actionError: null,
    });

    try {
      await dotvotingApi.setIdeaColumn(boardId, ideaId, columnId);
      publishBoardChange(boardId, 'topicIdeaColumnChanged', { ideaId, columnId });
    } catch (error) {
      set({
        ideas: previousIdeas,
        viewCard: syncViewCard(previousIdeas, get().viewCard),
        actionError: normalizeError(error),
      });
    }
  },
  removeIdea: async (ideaId) => {
    const { boardId } = get();
    if (!boardId || !hasBoardAccess()) {
      return;
    }

    try {
      await dotvotingApi.deleteIdea(boardId, ideaId);
      set((state) => {
        const ideas = state.ideas.filter((idea) => idea.id !== ideaId);

        return {
          ideas,
          viewCard: syncViewCard(ideas, state.viewCard),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'topicIdeaDeleted', {
        idea: {
          id: ideaId,
          topicId: boardId,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  addVote: async (ideaId) => {
    const currentUser = useUserStore.getState().getCurrentUser();
    const { boardDetails, boardId, ideas } = get();
    if (
      !boardId ||
      !currentUser ||
      boardDetails?.locked ||
      !boardDetails?.votingEnabled ||
      boardDetails.votingStyle === 'none' ||
      !hasBoardUser()
    ) {
      return;
    }

    if (boardDetails.cardsRequireApproval === true) {
      const targetIdea = ideas.find((idea) => idea.id === ideaId);
      if (targetIdea && targetIdea.approved === false) {
        return;
      }
    }

    if (boardDetails.votingStyle === 'dotvoting' && get().hasExceededVoteLimit(currentUser.id)) {
      return;
    }

    if (boardDetails.votingStyle === 'liking') {
      const targetIdea = ideas.find((idea) => idea.id === ideaId);
      if (!targetIdea) {
        return;
      }

      const hasUserLike = targetIdea.votes.some((vote) => vote.ownerId === currentUser.id);
      if (hasUserLike) {
        return;
      }
    }

    const votingStyle = boardDetails.votingStyle;

    try {
      const vote = await dotvotingApi.addVote(boardId, ideaId);
      set((state) => ({
        ideas: state.ideas.map((idea) => {
          if (
            idea.id !== ideaId ||
            idea.votes.some((existingVote) => existingVote.id === vote.id)
          ) {
            return idea;
          }

          if (votingStyle === 'liking') {
            const preservedVotes = idea.votes.filter(
              (existingVote) => existingVote.ownerId !== currentUser.id
            );
            return {
              ...idea,
              votes: [...preservedVotes, vote],
            };
          }

          return {
            ...idea,
            votes: [...idea.votes, vote],
          };
        }),
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicCandidateVoted', {
        vote: {
          ...vote,
          ideaId,
        },
      });
      trackParticipantContribution(boardId, boardDetails, 'vote');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  removeVote: async (ideaId, voteId) => {
    const { boardId, boardDetails } = get();
    if (
      !boardId ||
      boardDetails?.locked ||
      !boardDetails?.votingEnabled ||
      boardDetails.votingStyle === 'none' ||
      !hasBoardAccess()
    ) {
      return;
    }

    try {
      await dotvotingApi.removeVote(boardId, ideaId, voteId);
      set((state) => ({
        ideas: state.ideas.map((idea) =>
          idea.id === ideaId
            ? { ...idea, votes: idea.votes.filter((vote) => vote.id !== voteId) }
            : idea
        ),
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicCandidateVoteRemoved', {
        vote: {
          id: voteId,
          ideaId,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  addComment: async (ideaId, content) => {
    const { boardId, boardDetails } = get();
    if (!boardId || boardDetails?.locked || !boardDetails?.commentsEnabled || !hasBoardUser()) {
      return;
    }

    try {
      const comment = await dotvotingApi.addComment(boardId, ideaId, content);
      set((state) => {
        const updatedIdeas = state.ideas.map((idea) =>
          idea.id === ideaId ? { ...idea, comments: [...idea.comments, comment] } : idea
        );

        return {
          ideas: updatedIdeas,
          viewCard: syncViewCard(updatedIdeas, state.viewCard),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'ideaCommentAdded', {
        comment: {
          ...comment,
          ideaId,
        },
      });
      trackParticipantContribution(boardId, boardDetails, 'comment');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  removeComment: async (ideaId, commentId) => {
    const { boardId, boardDetails } = get();
    if (!boardId || !boardDetails?.commentsEnabled || !hasBoardAccess()) {
      return;
    }

    try {
      await dotvotingApi.deleteComment(boardId, ideaId, commentId);
      set((state) => {
        const updatedIdeas = state.ideas.map((idea) =>
          idea.id === ideaId
            ? {
                ...idea,
                comments: idea.comments.filter((comment) => comment.id !== commentId),
              }
            : idea
        );

        return {
          ideas: updatedIdeas,
          viewCard: syncViewCard(updatedIdeas, state.viewCard),
          actionError: null,
        };
      });
      publishBoardChange(boardId, 'ideaCommentDeleted', {
        comment: {
          id: commentId,
          ideaId,
          topicId: boardId,
        },
      });
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  reorderIdeaSortOrder: async (cardOrder) => {
    const { boardId, boardDetails, ideas } = get();
    if (
      !boardId ||
      !boardDetails?.isOwner ||
      boardDetails.cardSorting !== 'custom' ||
      boardDetails.locked ||
      !hasBoardAccess()
    ) {
      return;
    }

    const knownIdeaIds = new Set(ideas.map((idea) => idea.id));
    const sanitizedCardOrder = Array.from(
      new Set(cardOrder.filter((cardId) => knownIdeaIds.has(cardId)))
    );

    if (sanitizedCardOrder.length === 0) {
      return;
    }

    const previousIdeas = ideas;
    const updatedIdeas = applyCardOrderToIdeas(previousIdeas, sanitizedCardOrder);
    set({
      ideas: updatedIdeas,
      viewCard: syncViewCard(updatedIdeas, get().viewCard),
      actionError: null,
    });

    try {
      await dotvotingApi.reorderIdeaSortOrder(boardId, sanitizedCardOrder);
      publishBoardChange(boardId, 'sortTopicCards', { cardIds: sanitizedCardOrder });
    } catch (error) {
      set({
        ideas: previousIdeas,
        viewCard: syncViewCard(previousIdeas, get().viewCard),
        actionError: normalizeError(error),
      });
    }
  },
  addChatMessage: async (content) => {
    const { boardId, boardDetails } = get();
    const currentUser = useUserStore.getState().getCurrentUser();
    const trimmedContent = content.trim();

    if (!boardId || boardDetails?.locked || !currentUser || !trimmedContent || !hasBoardUser()) {
      return;
    }

    try {
      const chat = await dotvotingApi.addChat(boardId, currentUser.name, trimmedContent);
      set((state) => ({
        chat: [...state.chat, chat],
        actionError: null,
      }));
      publishBoardChange(boardId, 'topicChatAdded', { chat });
      trackParticipantContribution(boardId, boardDetails, 'chat');
    } catch (error) {
      set({ actionError: normalizeError(error) });
    }
  },
  setViewCard: (idea) => set({ viewCard: idea }),
});
